import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import { createAppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { bsc, bscTestnet } from '@reown/appkit/networks';
import { getCurrentNetwork, switchToNetwork, isValidNetwork, getNetworkByChainId } from '../../config/blockchain';
import { balanceManager } from './balanceManager';
import { registrationManager } from './registrationManager';
import { walletConnectionManager } from './walletConnection';
import { PendingTransactionTracker } from '../../utils/PendingTransactionTracker';
import { authApi } from '../../api/auth';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  // State variables
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState('0');
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [currentNetwork, setCurrentNetwork] = useState(getCurrentNetwork());
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [isUserRegistered, setIsUserRegistered] = useState(null);
  const [registrationChecked, setRegistrationChecked] = useState(false);
  const [username, setUsername] = useState(null);
  const [providerType, setProviderType] = useState(null);
  const [appKit, setAppKit] = useState(null);

  const shouldUseWalletCalls = useCallback(() => {
    const storedUser = authApi.getCurrentUser();
    const walletAddress = storedUser?.wallet_address || storedUser?.walletAddress;
    return typeof walletAddress === 'string' && walletAddress.trim().length > 0;
  }, []);

  // Create setters object for easier passing to managers
  const setters = {
    setAccount, setProvider, setSigner, setIsConnected, setProviderType,
    setChainId, setIsCorrectNetwork, setCurrentNetwork, setBalance, setUsdtBalance,
    setIsUserRegistered, setRegistrationChecked, setUsername,
    account, provider, signer, currentNetwork, usdtBalance, providerType, appKit
  };

  // Initialize AppKit
  useEffect(() => {
    const initAppKit = async () => {
      try {
        
        // Always initialize AppKit, even with placeholder project ID
        // The 403 errors from API calls are handled silently by AppKit itself
        const projectId = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || 'your-project-id';
        const ethersAdapter = new EthersAdapter();
        const defaultNet = getCurrentNetwork();
        const defaultNetworkConfig = defaultNet.chainId === 56 ? bsc : bscTestnet;
        const origin = typeof window !== 'undefined' ? window.location.origin : '';

        // Minimal AppKit config (like goat_frontend) – default behaviour = correct WalletConnect flow on mobile
        const appKitInstance = createAppKit({
          adapters: [ethersAdapter],
          projectId,
          networks: [bsc, bscTestnet],
          defaultNetwork: defaultNetworkConfig,
          metadata: {
            name: 'GOAT Platform',
            description: 'Greatest of All Time - Investment Platform',
            url: origin || 'https://example.com',
            icons: [`${origin}/icon.png`]
          },
          features: {
            analytics: false,
            email: false,
            socials: []
          },
          themeMode: 'dark',
          themeVariables: {
            '--apkt-z-index': 99999,
            '--apkt-border-radius-master': '12px'
          }
        });
        
        setAppKit(appKitInstance);
        
        // Set the initial network state
        setCurrentNetwork(defaultNet);
        setIsCorrectNetwork(true); // Since we're using the correct default network
        
        // Subscribe to AppKit events
        // Wrap in try-catch to handle any errors silently
        try {
          appKitInstance.subscribeAccount((account) => {
            try {
              if (account?.isConnected && account?.address) {
                // Handle AppKit connection by updating wallet state
                setAccount(account.address);
                setIsConnected(true);
                setProviderType('appkit');
                
                // Get provider and signer from AppKit
                const ethersProvider = appKitInstance.getWalletProvider();
                if (ethersProvider) {
                  const provider = new ethers.BrowserProvider(ethersProvider);
                  setProvider(provider);
                  
                  provider.getSigner().then(signer => {
                    setSigner(signer);
                  }).catch(() => {});
                  
                  // Get network info
                  provider.getNetwork().then(network => {
                    const chainId = Number(network.chainId);
                    setChainId(chainId);
                    
                    const networkValid = isValidNetwork(chainId);
                    setIsCorrectNetwork(networkValid);
                    
                    if (networkValid) {
                      const networkInfo = getNetworkByChainId(chainId);
                      setCurrentNetwork(networkInfo);
                      
                      // Get balances
                      provider.getBalance(account.address).then(balance => {
                        setBalance(ethers.formatEther(balance));
                      }).catch(() => {});
                      
                      if (shouldUseWalletCalls()) {
                        balanceManager.getUSDTBalanceWithParams(account.address, provider, networkInfo).then(usdtBal => {
                          setUsdtBalance(usdtBal);
                        }).catch(() => {});
                      } else {
                        setUsdtBalance('0');
                      }
                    }
                  }).catch(() => {});
                }
                
                // Check user registration
                if (shouldUseWalletCalls()) {
                  debouncedCheckUserRegistration(account.address);
                } else {
                  setIsUserRegistered(false);
                  setRegistrationChecked(true);
                  setUsername(null);
                }
                
                // Clear manual disconnect flag
                localStorage.removeItem('wallet_manually_disconnected');
                localStorage.setItem('wallet_auto_connect', 'true');
                
              } else if (!account?.isConnected) {
                // Handle disconnection
                setAccount(null);
                setProvider(null);
                setSigner(null);
                setIsConnected(false);
                setChainId(null);
                setBalance('0');
                setUsdtBalance('0');
                setCurrentNetwork(null);
                setIsCorrectNetwork(false);
                setIsUserRegistered(null);
                setRegistrationChecked(false);
                setUsername(null);
                setProviderType(null);
              }
            } catch (error) {
              // Ignore errors in account subscription - wallet is optional
            }
          });
        } catch (error) {
          // Ignore errors in AppKit subscription setup - wallet is optional
        }

        try {
          appKitInstance.subscribeState((state) => {
            // Handle state changes here if needed
          });
        } catch (error) {
          // Ignore errors in state subscription - wallet is optional
        }

      } catch (error) {
        // Ignore AppKit initialization errors - wallet is optional
        // AppKit will still be available for manual connections
      }
    };

    initAppKit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create debounced registration check function
  const debouncedCheckUserRegistration = useMemo(() => {
    const inner = registrationManager.createDebouncedCheck(setIsUserRegistered, setRegistrationChecked, setUsername);
    return (address) => {
      if (!shouldUseWalletCalls()) {
        setIsUserRegistered(false);
        setRegistrationChecked(true);
        setUsername(null);
        return;
      }
      inner(address);
    };
  }, [setIsUserRegistered, setRegistrationChecked, setUsername, shouldUseWalletCalls]);

  // Auto-connect on app start
  // MetaMask connection is optional - only attempt if user has explicitly connected before
  useEffect(() => {
    const checkConnection = async () => {
      try {
        // Check for any previous wallet connection - only auto-connect if explicitly saved
        const hasStoredConnection = localStorage.getItem('wallet_auto_connect') === 'true';
        const wasManuallyDisconnected = localStorage.getItem('wallet_manually_disconnected') === 'true';
        
        // Only attempt auto-connect if user has explicitly enabled it and hasn't manually disconnected
        if (!hasStoredConnection || wasManuallyDisconnected) {
          return;
        }
        
        // Check if MetaMask is available (optional check)
        const hasMetaMask = typeof window.ethereum !== 'undefined' && window.ethereum.selectedAddress && window.ethereum.isMetaMask;
        
        // Only attempt connection if MetaMask is available
        // Fail silently if connection fails - MetaMask is optional
        if (hasMetaMask) {
          try {
            await walletConnectionManager.autoConnectWallet(appKit, setters, debouncedCheckUserRegistration, shouldUseWalletCalls);
          } catch (error) {
            // Ignora tutti gli errori di auto-connect silenziosamente
            // MetaMask è opzionale, quindi non è un problema se la connessione fallisce
            // L'utente può connettersi manualmente se necessario
          }
        }
      } catch (error) {
        // Ignora tutti gli errori di check connection
        // MetaMask è opzionale, quindi non è un problema se qualcosa va storto
      }
    };
    
    // Delay the auto-connect check to ensure all functions are initialized
    const timeoutId = setTimeout(checkConnection, 100);
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      
      // Cleanup managers
      balanceManager.cleanup();
      registrationManager.cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKit, debouncedCheckUserRegistration]);

  // ✅ Set up page visibility listener for mobile MetaMask recovery
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[WALLET] Page became visible - checking for pending transactions');
        
        // When user returns to the app, check for pending transactions
        const unconfirmed = PendingTransactionTracker.getUnconfirmedTransactions();
        if (unconfirmed.length > 0 && account) {
          console.log(`[WALLET] Found ${unconfirmed.length} pending transactions on return`);
          // Trigger a refresh of dashboard data
          if (shouldUseWalletCalls()) {
            debouncedCheckUserRegistration(account);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, debouncedCheckUserRegistration]);

  // Wallet connection functions
  const connectWallet = useCallback(async () => {
    return await walletConnectionManager.connectWalletConnect(appKit, setters, debouncedCheckUserRegistration, shouldUseWalletCalls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKit, debouncedCheckUserRegistration]);

  const disconnectWallet = useCallback(async (silent = false) => {
    await walletConnectionManager.disconnectWallet(setters, silent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Legacy function kept for compatibility
  const reconnectWallet = useCallback(async () => {
    return await connectWallet();
  }, [connectWallet]);

  // Network switching
  const switchNetwork = useCallback(async (targetNetwork) => {
    return await switchToNetwork(targetNetwork);
  }, []);

  const switchToBSC = useCallback(async () => {
    return await walletConnectionManager.switchToBSC(currentNetwork, isCorrectNetwork);
  }, [currentNetwork, isCorrectNetwork]);

  // Balance functions
  const getUSDTBalance = useCallback(async (walletAddress) => {
    if (!shouldUseWalletCalls()) {
      return '0';
    }
    return await balanceManager.getUSDTBalance(walletAddress, provider, currentNetwork, usdtBalance);
  }, [provider, currentNetwork, usdtBalance, shouldUseWalletCalls]);

  // Transaction function
  const sendTransaction = useCallback(async (to, amount) => {
    try {
      const tx = await balanceManager.sendUSDTTransaction(signer, currentNetwork, to, amount);
      return {
        success: true,
        hash: tx.hash,
        transaction: tx
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        hash: null
      };
    }
  }, [signer, currentNetwork]);

  // Registration functions
  const checkUserRegistration = useCallback(async (address) => {
    if (!shouldUseWalletCalls()) {
      setIsUserRegistered(false);
      setRegistrationChecked(true);
      setUsername(null);
      return;
    }
    await registrationManager.checkUserRegistration(address, setIsUserRegistered, setRegistrationChecked, setUsername);
  }, [setIsUserRegistered, setRegistrationChecked, setUsername, shouldUseWalletCalls]);

  const refreshUserData = useCallback(() => {
    if (account) {
      if (shouldUseWalletCalls()) {
        debouncedCheckUserRegistration(account);
      }
    }
  }, [account, debouncedCheckUserRegistration]);

  // Signer function
  const ensureSigner = useCallback(async () => {
    return await walletConnectionManager.ensureSigner(signer, appKit, setProvider, setSigner);
  }, [signer, appKit]);

  // Context value - memoized to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // State
    account,
    provider,
    signer,
    isConnected,
    chainId,
    balance,
    usdtBalance,
    currentNetwork,
    isCorrectNetwork,
    isUserRegistered,
    registrationChecked,
    username,
    providerType,
    appKit,

    // Connection functions
    connectWallet,
    disconnectWallet,
    reconnectWallet,

    // Network functions
    switchNetwork,
    switchToBSC,

    // Balance functions
    getUSDTBalance,
    sendTransaction,

    // Registration functions
    checkUserRegistration,
    refreshUserData,

    // Utility functions
    ensureSigner,
  }), [
    account, provider, signer, isConnected, chainId, balance, usdtBalance,
    currentNetwork, isCorrectNetwork, isUserRegistered, registrationChecked,
    username, providerType, appKit, connectWallet, disconnectWallet,
    reconnectWallet, switchNetwork, switchToBSC, getUSDTBalance,
    sendTransaction, checkUserRegistration, refreshUserData, ensureSigner
  ]);

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export default WalletContext; 